import { useQuery } from 'react-query';
import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import registryApi from '../api/registry.api';
import {
  RegistryCategoryMatrixID,
  RegistryItemStatus,
  RegistryItems,
  RegistryItemsResponseType,
} from '../types/registry';
import { DefaultErrorMessage } from '../displayStrings';

// The endpoint lists every version, and each option is keyed on the matrix id all versions
// share, so an item with both an effective and an archived version has to collapse to one.
const dedupeByMatrixId = (items: RegistryItems[]) => {
  const itemsByMatrixId = new Map<string, RegistryItems>();

  items.forEach((item) => {
    const existing = itemsByMatrixId.get(item.registry_item_matrix_id);
    if (!existing || item.status === RegistryItemStatus.Effective) {
      itemsByMatrixId.set(item.registry_item_matrix_id, item);
    }
  });

  return [...itemsByMatrixId.values()];
};

const transformData = (data: RegistryItemsResponseType, eventItemIds: string[]) => {
  const linkableItems = (data?.items ?? []).filter(
    (item) =>
      item.status === RegistryItemStatus.Effective ||
      (eventItemIds.includes(item.registry_item_matrix_id) && item.status === RegistryItemStatus.Archived),
  );

  return dedupeByMatrixId(linkableItems).map((item) => ({
    id: item.registry_item_matrix_id,
    name: item.name,
    description: item.description ?? '',
    companyId: item.company_id.toString(),
    status: item.status,
    displayId: item.display_id,
  }));
};
export const useRegistryItems = (categoryId: string, eventItemIds: string[] = []) => {
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();

  return useQuery({
    queryKey: ['registry-items', companyId, categoryId],
    queryFn: () => registryApi.getRegistryItemsByCategory(companyId, categoryId),
    enabled: !!companyId && !!categoryId,
    staleTime: 5 * 60 * 1000,
    select: (data: RegistryItemsResponseType) => transformData(data, eventItemIds),
    onError: () => {
      showToast({
        title: `Could not retrieve ${
          categoryId === RegistryCategoryMatrixID.PRODUCT_MATRIX_ID ? 'products' : 'root causes'
        }`,
        description: DefaultErrorMessage,
        status: 'error',
      });
    },
  });
};
